import { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { Document, Page, pdfjs } from "react-pdf";

import { apiFetch } from "../../config/api";

import "./index.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const getInvoiceTitle = (invoiceType) =>
  invoiceType === "EST"
    ? "Estimate"
    : invoiceType === "DRW"
    ? "Partial Invoice"
    : "Invoice";

const getFilename = (headers, invoiceId) => {
  const disposition = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || `invoice_${invoiceId}.pdf`;
};

const PdfPreviewModal = ({ isOpen, onRequestClose, invoiceId, title }) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageWidth, setPageWidth] = useState(0);
  const viewerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      return undefined;
    }

    let active = true;
    let objectUrl = "";

    setIsLoading(true);
    setError("");
    setNumPages(0);

    apiFetch(`generate-invoice/${invoiceId}/`, { responseType: "blob" })
      .then(({ data, headers }) => {
        if (!active) return;
        objectUrl = window.URL.createObjectURL(data);
        setPdfUrl(objectUrl);
        setFilename(getFilename(headers, invoiceId));
      })
      .catch(() => {
        if (active) setError("The PDF could not be loaded. Please try again.");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setPdfUrl("");
      setFilename("");
    };
  }, [isOpen, invoiceId]);

  useEffect(() => {
    if (!isOpen || !viewerRef.current) return undefined;
    const updateWidth = () => setPageWidth(viewerRef.current?.clientWidth || 0);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [isOpen, pdfUrl]);

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="pdf-preview-modal"
      overlayClassName="pdf-preview-modal-overlay"
      contentLabel={`${title} PDF preview`}
    >
      <header className="pdf-preview-modal-header">
        <h2>{title}</h2>
        <div className="pdf-preview-modal-actions">
          <button type="button" onClick={downloadPdf} disabled={!pdfUrl}>
            Download
          </button>
          <button type="button" onClick={onRequestClose} aria-label="Close PDF preview">
            Close
          </button>
        </div>
      </header>
      <div className="pdf-preview-modal-viewer" ref={viewerRef}>
        {isLoading && <p role="status">Loading PDF…</p>}
        {error && <p role="alert">{error}</p>}
        {pdfUrl && (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages: count }) => setNumPages(count)}
            onLoadError={() => setError("The PDF could not be displayed.")}
            loading={<p role="status">Preparing preview…</p>}
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                key={index + 1}
                pageNumber={index + 1}
                width={pageWidth || undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>
        )}
      </div>
    </Modal>
  );
};

export default PdfPreviewModal;
export { getInvoiceTitle };
